import { useCallback, useState } from 'react';
import axios from 'axios';
import { generateErrorMsg, sendNotification } from 'utils/helperMethods';
import { customGroupsUrls } from 'config/urls';
import { queryClient } from 'services/reactQueryService';
import { ErrorMessages } from 'utils/Enums';
import { CUSTOM_GROUPS_QUERY_KEY } from '../constants';
import type { CustomProductGroupRecord } from '../types';

function useCustomProductGroupDelete(onMutationSuccess?: () => void) {
    const [recordToDelete, setRecordToDelete] = useState<CustomProductGroupRecord | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [isLastItem, setIsLastItem] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);

    const handleDelete = useCallback(async (record: CustomProductGroupRecord) => {
        setIsLastItem(false);
        setRecordToDelete(record);
        setPreviewLoading(true);
        try {
            const { data } = await axios.get<{ is_last: boolean; group_id: number }>(
                customGroupsUrls.removeItemsPreviewUrl(record.group_id, record.dim1_id),
            );
            setIsLastItem(data?.is_last ?? false);
        } catch {
            // show the modal anyway with the default subtitle
        } finally {
            setPreviewLoading(false);
        }
    }, []);

    const confirmDelete = useCallback(async () => {
        if (!recordToDelete) return;
        setDeleteLoading(true);
        try {
            await axios.post(customGroupsUrls.removeItemsUrl(recordToDelete.group_id), {
                dim1_ids: [recordToDelete.dim1_id],
            });
            sendNotification('success', 'Record removed successfully.');
            queryClient.removeQueries({ queryKey: [CUSTOM_GROUPS_QUERY_KEY], exact: false });
            onMutationSuccess?.();
            setRecordToDelete(null);
        } catch (error: any) {
            sendNotification(
                'error',
                generateErrorMsg(String(error?.response?.data?.detail || ''), ErrorMessages.GENERALIZED_ERROR_MSG),
            );
        } finally {
            setDeleteLoading(false);
        }
    }, [recordToDelete]);

    return {
        recordToDelete,
        setRecordToDelete,
        deleteLoading,
        previewLoading,
        isLastItem,
        handleDelete,
        confirmDelete,
    };
}

export default useCustomProductGroupDelete;
